/**
 * Envoi des demandes Sahha Consult et devis MRE vers POST /api/leads, en JSON.
 *
 * Bug corrigé le 06/10/2026 : le formulaire Sahha Consult (/consult/commander)
 * et le devis MRE (/mre/procedures/[slug]) étaient des <form action="/api/leads">
 * natifs. Le navigateur envoyait du application/x-www-form-urlencoded, la route
 * fait `request.json()` : SyntaxError, 500 « Erreur serveur » affiché en JSON
 * brut, demande perdue. En plus, même en JSON, « OTHER » (5 caractères) cassait
 * mreCountryFrom (max 4) et les champs du bon Consult (pack, bénéficiaire…)
 * n'existent pas dans le schéma : Zod les aurait jetés en silence.
 *
 * Module sans dépendance serveur (importé par des composants client) :
 *  - les constructeurs reproduisent le contrat de `leadSchema`
 *    (src/app/api/leads/route.ts — non exportable, un route.ts n'exporte que
 *    ses handlers) ; tests/node/lead-forms-payload.test.ts fait passer chaque
 *    corps construit ici dans le VRAI handler POST ;
 *  - la validation locale suit celle du serveur : téléphone = isValidMarPhone
 *    (même règle que phoneMar, testée dans lab-format.test.ts), e-mail = motif
 *    de z.string().email() (zod 3) ;
 *  - la lecture de la réponse distingue succès, champs refusés (400 +
 *    fieldErrors), 429, erreur serveur et panne réseau.
 *
 * Rangé sous components/mre : Sahha Consult est le produit « bons d'achat MRE ».
 */
import { isValidMarPhone } from "@/lib/labs/format";
import { normalizeMarOrIntlPhone } from "@/lib/validation/phone-format";

/** Bornes de `leadSchema` (route.ts). */
export const LEAD_LIMITS = { name: 100, message: 2000, countryCode: 4, source: 500 } as const;

export type LeadLocale = "fr" | "ar" | "en";

/** Sous-ensemble de `leadSchema` utilisé par ces deux formulaires. */
export type LeadRequestBody = {
  type: "MRE_QUOTE" | "OTHER";
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  message?: string;
  mreProcedureSlug?: string;
  mreCountryFrom?: string;
  source?: string;
  preferredLanguage?: LeadLocale;
};

export function toLeadLocale(locale: string): LeadLocale {
  return locale === "ar" || locale === "en" ? locale : "fr";
}

/**
 * Texte nettoyé : bords retirés, vide → absent (le schéma refuse "" sur les
 * champs min(1) et email), borné à `max` caractères.
 */
function clean(value: string | null | undefined, max = Number.POSITIVE_INFINITY): string | undefined {
  const v = (value ?? "").trim();
  return v ? v.slice(0, max) : undefined;
}

/**
 * Téléphone compacté avant envoi : chiffres arabes-indiens (٠-٩, ۰-۹ — clavier
 * arabe du téléphone) ramenés en chiffres latins, puis séparateurs retirés
 * comme le fait normalizeMarPhone. Sans ça, « ٠٦١٢٣٤٥٦٧٨ » est refusé et
 * « +212 - 6 - 12 - 34 - 56 - 78 » dépasse la borne brute max(20) du serveur.
 */
export function compactPhone(raw: string): string {
  return raw
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[\s\-().]/g, "");
}

// Même motif que z.string().email() en zod 3 (emailRegex) : le serveur refuse
// « nom@domaine » sans extension, que le navigateur accepte en type="email".
const EMAIL_RE = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i;

export function isLeadEmail(raw: string): boolean {
  return EMAIL_RE.test(raw.trim());
}

export type ContactFields = { firstName: string; lastName: string; email: string; phone: string };
export type ContactErrorCode = "required" | "emailInvalid" | "phoneInvalid";

/**
 * Prénom, nom, e-mail et téléphone sont obligatoires sur les deux formulaires.
 * Téléphone : marocain (contrat phoneMar) par défaut ; `international` pour le
 * devis MRE, où /api/leads accepte aussi les numéros étrangers (phoneMarOrIntl,
 * même normalisation que le serveur — sinon le navigateur bloquait « +33… »).
 */
export function validateContact(
  f: ContactFields,
  opts: { international?: boolean } = {},
): Partial<Record<keyof ContactFields, ContactErrorCode>> {
  const errs: Partial<Record<keyof ContactFields, ContactErrorCode>> = {};
  if (!f.firstName.trim()) errs.firstName = "required";
  if (!f.lastName.trim()) errs.lastName = "required";
  const email = f.email.trim();
  if (!email) errs.email = "required";
  else if (!isLeadEmail(email)) errs.email = "emailInvalid";
  const phone = compactPhone(f.phone);
  if (!phone) errs.phone = "required";
  else if (!(opts.international ? normalizeMarOrIntlPhone(phone) !== null : isValidMarPhone(phone))) errs.phone = "phoneInvalid";
  return errs;
}

function contactBody(f: ContactFields) {
  return {
    firstName: clean(f.firstName, LEAD_LIMITS.name),
    lastName: clean(f.lastName, LEAD_LIMITS.name),
    email: clean(f.email),
    phone: compactPhone(f.phone),
  };
}

/* ─── Sahha Consult (/consult/commander) ───────────────────────────────── */

export const CONSULT_ORDER_SOURCE = "/consult/commander";

export type ConsultOrderInput = {
  packId: string;
  packPrice: number;
  paymentMethod: string;
  buyer: ContactFields;
  recipient: ContactFields;
  personalMessage: string;
};

/**
 * Le schéma n'a pas de champ pour le pack, le règlement ni le bénéficiaire :
 * ils vont dans `message` (lu par l'équipe dans l'alerte e-mail et l'admin).
 * Libellés en français : c'est une note interne, la langue du visiteur part
 * dans preferredLanguage. Type OTHER + source /consult/commander : inchangés
 * par rapport à l'ancien formulaire.
 */
export function buildConsultOrderLead(input: ConsultOrderInput, locale: string): LeadRequestBody {
  const r = input.recipient;
  const recipient = [
    clean(`${clean(r.firstName, LEAD_LIMITS.name) ?? ""} ${clean(r.lastName, LEAD_LIMITS.name) ?? ""}`),
    clean(r.email, 254),
    clean(r.phone, 30),
  ]
    .filter(Boolean)
    .join(" · ");
  const note = clean(input.personalMessage, 300);
  const lines = [
    "[Sahha Consult] Demande de bon",
    `• Pack : ${input.packId} (${input.packPrice} MAD)`,
    `• Règlement souhaité : ${input.paymentMethod}`,
    `• Bénéficiaire : ${recipient || "l'acheteur (champs laissés vides)"}`,
    ...(note ? [`• Message personnel : ${note}`] : []),
  ];
  return {
    type: "OTHER",
    ...contactBody(input.buyer),
    message: lines.join("\n").slice(0, LEAD_LIMITS.message),
    source: CONSULT_ORDER_SOURCE,
    preferredLanguage: toLeadLocale(locale),
  };
}

/* ─── Devis MRE (/mre/procedures/[slug]) ────────────────────────────────── */

/** Pays proposés dans le formulaire de devis (libellés : mre.detail.quoteForm.countries.*). */
export const MRE_QUOTE_COUNTRY_CODES = ["FR", "BE", "CH", "CA", "GB", "NL", "DE", "ES", "OTHER"] as const;

/** Code du formulaire → valeur acceptée par mreCountryFrom (max 4) : « OTHER » devient « OTH », comme MrePackRequestForm. */
export function toLeadCountryCode(code: string): string | undefined {
  const c = code.trim().toUpperCase();
  if (!c) return undefined;
  return c === "OTHER" ? "OTH" : c.slice(0, LEAD_LIMITS.countryCode);
}

export type MreQuoteInput = ContactFields & { countryFrom: string; message: string };

/**
 * L'alerte e-mail de l'équipe n'affiche ni la procédure ni le pays (champs
 * mre* stockés en base mais absents du gabarit) : une ligne d'en-tête les
 * reprend dans `message`, avant le texte du visiteur.
 */
export function buildMreQuoteLead(
  input: MreQuoteInput,
  ctx: { procedureSlug: string; locale: string },
): LeadRequestBody {
  const country = toLeadCountryCode(input.countryFrom);
  const header = `[Devis MRE] Procédure : ${ctx.procedureSlug} · Pays de résidence : ${country ?? "—"}`;
  const project = clean(input.message, Math.max(0, LEAD_LIMITS.message - header.length - 1));
  return {
    type: "MRE_QUOTE",
    ...contactBody(input),
    message: (project ? `${header}\n${project}` : header).slice(0, LEAD_LIMITS.message),
    mreProcedureSlug: ctx.procedureSlug,
    mreCountryFrom: country,
    source: `/mre/procedures/${ctx.procedureSlug}`.slice(0, LEAD_LIMITS.source),
    preferredLanguage: toLeadLocale(ctx.locale),
  };
}

/* ─── Envoi et lecture de la réponse ───────────────────────────────────── */

export type LeadOutcome =
  | { kind: "success" }
  | { kind: "invalid"; fields: string[] }
  | { kind: "rateLimited" }
  | { kind: "network" }
  | { kind: "error" };

/**
 * Succès = 2xx ET `success: true` : une page HTML servie en 200 (redirection,
 * page d'erreur d'un proxy) ne doit pas passer pour une demande enregistrée.
 */
export function interpretLeadResponse(status: number, body: unknown): LeadOutcome {
  const data = (body && typeof body === "object" ? body : {}) as {
    success?: unknown;
    errors?: { fieldErrors?: Record<string, unknown> };
  };
  if (status >= 200 && status < 300 && data.success === true) return { kind: "success" };
  if (status === 429) return { kind: "rateLimited" };
  if (status === 400) {
    const fieldErrors = data.errors?.fieldErrors ?? {};
    const fields = Object.entries(fieldErrors)
      .filter(([, v]) => Array.isArray(v) && v.length > 0)
      .map(([k]) => k);
    return { kind: "invalid", fields };
  }
  return { kind: "error" };
}

export async function postLead(body: LeadRequestBody, fetchImpl: typeof fetch = fetch): Promise<LeadOutcome> {
  let res: Response;
  try {
    res = await fetchImpl("/api/leads", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { kind: "network" };
  }
  const json: unknown = await res.json().catch(() => null);
  return interpretLeadResponse(res.status, json);
}

/**
 * Un seul envoi à la fois : un second submit (double clic, Entrée répétée)
 * pendant l'envoi renvoie « busy » sans requête. À créer une fois par
 * formulaire (useState(createLeadSender)).
 */
export function createLeadSender(fetchImpl?: typeof fetch) {
  let inFlight = false;
  return {
    get busy() {
      return inFlight;
    },
    async send(body: LeadRequestBody): Promise<LeadOutcome | { kind: "busy" }> {
      if (inFlight) return { kind: "busy" };
      inFlight = true;
      try {
        return await postLead(body, fetchImpl);
      } finally {
        inFlight = false;
      }
    },
  };
}
